// @flow
import React, { useState, Fragment } from 'react'
import styled, { type StyledComponent } from 'styled-components'
import type { Configs, ImageConfig } from './types'
import TagSwitcher from './AdEdit/ProviderPreviews/TagSwitcher'
import ProviderPreviews from './AdEdit/ProviderPreviews'
import type { PreviewData } from './AdEdit/NamedPreview'
import { extractDomainFromUrl } from './AdEdit/ProviderPreviews/prepareDataForPreview'
import UrlInput from './AdEdit/AdInputs/UrlInput'
import TextInput from './AdEdit/AdInputs/TextInput'
import LinkButtons from './AdEdit/AdInputs/LinkButtons'
import MediaLoader from './AdEdit/MediaLoader'
import PreviewImageCropper from './AdEdit/PreviewImageCropper'

export const VK_PUBLIC = {
  id: 0,
  label: 'Группа ВКонтакте',
  image_url: ''
}

export const FB_PUBLIC = {
  id: 0,
  label: 'Группа Facebook',
  image_url: ''
}

const FB_PLACEMENTS = {
  audience_network: ['classic'],
  facebook: ['feed', 'right_hand_column', 'story'],
  instagram: ['stream'],
  messenger: []
}

const InputsWrapper: StyledComponent<Object, any, any> = styled.div`
  max-width: 720px;
`
const Wrapper: StyledComponent<Object, any, any> = styled.div`
  min-width: 820px;
`
const Header: StyledComponent<Object, any, any> = styled.h2`
  text-align: left;
  padding: 20px 0;
`
const Row: StyledComponent<Object, any, any> = styled.div`
  padding-bottom: 14px;
`
const CropperSpacer: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  width: 5px;
`

type Props = {|
  configs: Configs
|}

export default function FreeService(props: Props) {
  const { configs } = props

  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [extendedDescription, setExtendedDescription] = useState('')
  const [vkLinkButton, setVkLinkButton] = useState('')
  const [tmLinkButton, setTmLinkButton] = useState('')
  const [fbLinkButton, setFbLinkButton] = useState('')
  const [image, setImage] = useState(null)
  const [vkCrop, setVkCrop] = useState(null)
  const [tmCrop, setTmCrop] = useState(null)
  const [fbCrop, setFbCrop] = useState(null)
  const [glCrop, setGlCrop] = useState(null)

  const data: PreviewData = {
    url: extractDomainFromUrl(url),
    title: title,
    vkTitle: title,
    fbTitle: title,
    description: description,
    vkExtendedDescription: extendedDescription || description,
    fbExtendedDescription: extendedDescription || description,
    image,
    vkImageCrop: vkCrop,
    tmImageCrop: tmCrop,
    fbImageCrop: fbCrop,
    glImageCrop: glCrop,
    linkTitle: '',
    logoImage: null,
    logoImageCrop: null,
    vkPublic: VK_PUBLIC,
    fbPublic: FB_PUBLIC,
    video: null,
    vkHasButton: true,
    tmSmallImage: null,
    tmSmallImageCrop: null,
    tmLinkButton,
    instagramImage: null,
    instagramImageCrop: null,
    fbLinkButton,
    vkLinkButton,
    fbThumbnailImage: null,
    fbThumbnailImageCrop: null,
    vkBigImage: null
  }

  return (
    <Wrapper className="t-FreeService">
      <Header>Заполните текстовые поля</Header>
      <Fragment>
        <InputsWrapper>
          <Row>
            <UrlInput value={url} header="Ссылка" onChange={setUrl} compact />
          </Row>
          <Row>
            <TextInput
              header="Заголовок"
              maxLength={configs.titleMaxLength}
              value={title}
              onChange={setTitle}
            />
          </Row>
          <Row>
            <TextInput
              header="Описание"
              maxLength={configs.descriptionMaxLength}
              value={description}
              onChange={setDescription}
            />
          </Row>
          <Row>
            <TextInput
              header="Расширенное описание (для ВКонтакте и Facebook)"
              maxLength={configs.vkExtendedDescriptionMaxLength}
              value={extendedDescription}
              multiline
              onChange={setExtendedDescription}
            />
          </Row>
        </InputsWrapper>
        <LinkButtons
          vkOptions={configs.vkWithoutPublicLinkButtonOptions}
          tmOptions={configs.tmLinkButtonOptions}
          fbOptions={configs.fbLinkButtonOptions}
          vkValue={vkLinkButton}
          tmValue={tmLinkButton}
          fbValue={fbLinkButton}
          onVkChange={setVkLinkButton}
          onTmChange={setTmLinkButton}
          onFbChange={setFbLinkButton}
        />
        <Header>Загрузите изображение</Header>
        <MediaLoader
          authenticityToken=""
          media={image}
          errors={[]}
          onMediasLoad={(medias: Array<Object>) => {
            setImage(medias[0])
            setVkCrop(null)
            setTmCrop(null)
            setFbCrop(null)
            setGlCrop(null)
          }}
          skipMediaUpload
          mediaType="image"
        />
        {image && (
          <Fragment>
            <CropperSpacer />
            <PreviewImageCropper
              image={image}
              config={freeCropConfig(configs.imageConfigCollection.promo.vk.sizesRelation)}
              crop={vkCrop}
              onChange={setVkCrop}
              title="ВКонтакте"
            />
            <CropperSpacer />
            <PreviewImageCropper
              image={image}
              config={freeCropConfig(configs.imageConfigCollection.promo.target_mail.sizesRelation)}
              crop={tmCrop}
              onChange={setTmCrop}
              title="myTarget"
            />
            <CropperSpacer />
            <PreviewImageCropper
              image={image}
              config={freeCropConfig(configs.imageConfigCollection.promo.facebook.sizesRelation)}
              crop={fbCrop}
              onChange={setFbCrop}
              title="Facebook"
            />
            <CropperSpacer />
            <PreviewImageCropper
              image={image}
              config={freeCropConfig(configs.imageConfigCollection.promo.google.sizesRelation)}
              crop={glCrop}
              onChange={setGlCrop}
              title="Google"
            />
          </Fragment>
        )}
        <Header>Предпросмотр</Header>
        <TagSwitcher
          namesRenderer={(names) => <ProviderPreviews names={names} data={data} />}
          providers={['vk', 'target_mail', 'facebook', 'google']}
          fbPlacements={FB_PLACEMENTS}
          maxPreviewTag={'multi'}
        />
      </Fragment>
    </Wrapper>
  )
}

function freeCropConfig(ratio: number): ImageConfig {
  return {
    sizesRelation: ratio,
    minWidth: 5,
    minHeight: 5,
    maxWidth: 10000,
    maxHeight: 10000
  }
}
